import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  useTherapistBookings,
  type Booking,
} from '@theraply/api-client';
import {
  MagnifyingGlassIcon,
  UserGroupIcon,
  CalendarIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  VideoCameraIcon,
  ChevronRightIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

interface ClientSummary {
  consumerId: number;
  name: string;
  email?: string;
  totalSessions: number;
  lastSessionDate: string;
  nextSessionDate?: string;
  nextSessionTime?: string;
  nextBookingId?: number;
  totalSpent: number;
  bookings: Booking[];
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTime(timeStr: string): string {
  const [hours, minutes] = timeStr.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
}

function formatCurrency(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function deriveClients(bookings: Booking[]): ClientSummary[] {
  const clientMap = new Map<number, ClientSummary>();
  const now = new Date();

  for (const booking of bookings) {
    const existing = clientMap.get(booking.consumerId);

    if (!existing) {
      const isUpcoming =
        booking.status === 'CONFIRMED' &&
        new Date(`${booking.slotDate}T${booking.slotTime}`) > now;

      clientMap.set(booking.consumerId, {
        consumerId: booking.consumerId,
        name: booking.consumerName || `Client #${booking.consumerId}`,
        email: booking.consumerEmail,
        totalSessions: booking.status === 'COMPLETED' || booking.status === 'CONFIRMED' ? 1 : 0,
        lastSessionDate: booking.slotDate,
        nextSessionDate: isUpcoming ? booking.slotDate : undefined,
        nextSessionTime: isUpcoming ? booking.slotTime : undefined,
        nextBookingId: isUpcoming ? booking.id : undefined,
        totalSpent: booking.status !== 'CANCELLED' ? booking.amountCents : 0,
        bookings: [booking],
      });
    } else {
      existing.bookings.push(booking);
      if (booking.status === 'COMPLETED' || booking.status === 'CONFIRMED') {
        existing.totalSessions += 1;
      }
      if (booking.status !== 'CANCELLED') {
        existing.totalSpent += booking.amountCents;
      }

      // Track last session (most recent past or completed)
      if (booking.slotDate > existing.lastSessionDate) {
        existing.lastSessionDate = booking.slotDate;
      }

      // Track next upcoming session
      const isUpcoming =
        booking.status === 'CONFIRMED' &&
        new Date(`${booking.slotDate}T${booking.slotTime}`) > now;
      if (isUpcoming) {
        if (
          !existing.nextSessionDate ||
          booking.slotDate < existing.nextSessionDate
        ) {
          existing.nextSessionDate = booking.slotDate;
          existing.nextSessionTime = booking.slotTime;
          existing.nextBookingId = booking.id;
        }
      }

      // Use name if available
      if (booking.consumerName) {
        existing.name = booking.consumerName;
      }
      if (booking.consumerEmail) {
        existing.email = booking.consumerEmail;
      }
    }
  }

  return Array.from(clientMap.values()).sort(
    (a, b) => b.lastSessionDate.localeCompare(a.lastSessionDate)
  );
}

export default function ClientsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClient, setSelectedClient] = useState<ClientSummary | null>(null);

  const { data, isLoading, error, refetch } = useTherapistBookings();

  const clients = useMemo(
    () => deriveClients(data?.bookings || []),
    [data?.bookings]
  );

  const filteredClients = useMemo(() => {
    if (!searchQuery.trim()) return clients;
    const query = searchQuery.toLowerCase();
    return clients.filter(
      (client) =>
        client.name.toLowerCase().includes(query) ||
        client.email?.toLowerCase().includes(query)
    );
  }, [clients, searchQuery]);

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading clients...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md text-center">
          <ExclamationTriangleIcon className="h-10 w-10 text-red-400 mx-auto mb-3" />
          <h3 className="text-red-800 font-semibold mb-2">Error Loading Clients</h3>
          <p className="text-red-600 text-sm mb-4">
            Failed to load your client list. Please try again.
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Clients</h1>
        <p className="mt-2 text-sm text-gray-600">
          Manage your client roster and view client information.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">Total Clients</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{clients.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">With Upcoming Sessions</p>
          <p className="text-2xl font-bold text-blue-600 mt-1">
            {clients.filter((c) => c.nextSessionDate).length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">Total Revenue</p>
          <p className="text-2xl font-bold text-green-600 mt-1">
            {formatCurrency(clients.reduce((sum, c) => sum + c.totalSpent, 0))}
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search clients by name or email..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Client List */}
      {filteredClients.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <UserGroupIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {searchQuery ? 'No clients found' : 'No clients yet'}
          </h3>
          <p className="text-gray-500 text-sm">
            {searchQuery
              ? 'Try adjusting your search terms.'
              : 'Clients will appear here once they book sessions with you.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow divide-y divide-gray-100">
          {filteredClients.map((client) => (
            <div
              key={client.consumerId}
              className="p-4 sm:p-6 hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={() => setSelectedClient(client)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                    <span className="text-lg font-semibold text-indigo-600">
                      {client.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">
                      {client.name}
                    </h3>
                    {client.email && (
                      <p className="text-sm text-gray-500">{client.email}</p>
                    )}
                    <div className="flex items-center gap-4 mt-1">
                      <span className="text-xs text-gray-500">
                        {client.totalSessions} session{client.totalSessions !== 1 ? 's' : ''}
                      </span>
                      <span className="text-xs text-gray-400">|</span>
                      <span className="text-xs text-gray-500">
                        Last: {formatDate(client.lastSessionDate)}
                      </span>
                      {client.nextSessionDate && (
                        <>
                          <span className="text-xs text-gray-400">|</span>
                          <span className="text-xs text-blue-600 font-medium">
                            Next: {formatDate(client.nextSessionDate)}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {client.nextBookingId && (
                    <Link
                      to={`/session/${client.nextBookingId}`}
                      onClick={(e) => e.stopPropagation()}
                      className="hidden sm:inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                    >
                      <VideoCameraIcon className="h-3.5 w-3.5 mr-1" />
                      Join Next
                    </Link>
                  )}
                  <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Client Detail Panel */}
      {selectedClient && (
        <ClientDetailPanel
          client={selectedClient}
          onClose={() => setSelectedClient(null)}
        />
      )}
    </div>
  );
}

function ClientDetailPanel({
  client,
  onClose,
}: {
  client: ClientSummary;
  onClose: () => void;
}) {
  const now = new Date();
  const sortedBookings = [...client.bookings].sort((a, b) => {
    const dateA = new Date(`${a.slotDate}T${a.slotTime}`);
    const dateB = new Date(`${b.slotDate}T${b.slotTime}`);
    return dateB.getTime() - dateA.getTime();
  });

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black bg-opacity-25" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 max-w-lg w-full bg-white shadow-xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
              <span className="text-lg font-semibold text-indigo-600">
                {client.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{client.name}</h2>
              {client.email && (
                <p className="text-sm text-gray-500">{client.email}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-md"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Client Stats */}
        <div className="px-6 py-4 grid grid-cols-3 gap-4 border-b border-gray-100">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{client.totalSessions}</p>
            <p className="text-xs text-gray-500">Sessions</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">
              {formatCurrency(client.totalSpent)}
            </p>
            <p className="text-xs text-gray-500">Total Revenue</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">
              {formatDate(client.lastSessionDate)}
            </p>
            <p className="text-xs text-gray-500">Last Session</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Quick Actions</h3>
          <div className="flex gap-2">
            <Link
              to="/messages"
              className="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1" />
              Message
            </Link>
            <Link
              to="/notes"
              className="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              <DocumentTextIcon className="h-4 w-4 mr-1" />
              Notes
            </Link>
          </div>
        </div>

        {/* Session History with this client */}
        <div className="px-6 py-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">
            Session History ({sortedBookings.length})
          </h3>
          <div className="space-y-3">
            {sortedBookings.map((booking) => {
              const isPast = new Date(`${booking.slotDate}T${booking.slotTime}`) <= now;
              const statusBadge = getStatusBadge(booking.status);
              return (
                <div
                  key={booking.id}
                  className="bg-gray-50 rounded-lg p-3"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">
                          {formatDate(booking.slotDate)}
                        </span>
                        <span className="text-sm text-gray-500">
                          {formatTime(booking.slotTime)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusBadge.className}`}>
                          {statusBadge.label}
                        </span>
                        <span className="text-xs text-gray-500">
                          {booking.sessionType === 'INTAKE'
                            ? 'Intake'
                            : booking.sessionType === 'CRISIS'
                            ? 'Crisis'
                            : 'Regular'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatCurrency(booking.amountCents)}
                        </span>
                      </div>
                    </div>
                    {!isPast && booking.status === 'CONFIRMED' && (
                      <Link
                        to={`/session/${booking.id}`}
                        className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
                      >
                        Join
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function getStatusBadge(status: Booking['status']) {
  switch (status) {
    case 'CONFIRMED':
      return { label: 'Confirmed', className: 'bg-green-100 text-green-800' };
    case 'COMPLETED':
      return { label: 'Completed', className: 'bg-blue-100 text-blue-800' };
    case 'CANCELLED':
      return { label: 'Cancelled', className: 'bg-red-100 text-red-800' };
    case 'PENDING_PAYMENT':
      return { label: 'Pending', className: 'bg-yellow-100 text-yellow-800' };
    default:
      return { label: status, className: 'bg-gray-100 text-gray-800' };
  }
}
